import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { listSignatureBackups, clearAllSignatureBackups } from "@/lib/signatureBackup";

const STORAGE_KEY = "wi-pa-wizard-v1";

/**
 * Hydrate signatures from the independent signature-backup store.
 *
 * If the wizard state was reset/wiped but the parallel backup still has
 * signature PNGs (e.g. user hit Discard, then immediately resumed; or a
 * browser crash between strokes and submit), restore those PNGs on boot.
 * The wizard JSON in localStorage is the primary source of truth, but the
 * backup is an authoritative fallback for ANY signature PNG that's missing
 * — never overwrites a value the wizard already has.
 *
 * Also writes a transient `_restored_from_backup` array listing which roles
 * came from the backup. SignaturePadField reads this on mount to display
 * the "Restored from backup" chip — without this hint the pad couldn't
 * distinguish a normal load from a recovery, since by the time it mounts
 * the wizard state has already been populated by this hydrator.
 */
function hydrateSignatureBackup(state) {
  try {
    const backups = listSignatureBackups();
    if (!backups || Object.keys(backups).length === 0) return state;
    const sig = { ...(state.signatures || {}) };
    const restoredRoles = [];
    for (const role of Object.keys(backups)) {
      const key = `${role}_signature_png`;
      if (!sig[key] && backups[role]?.data) {
        sig[key] = backups[role].data;
        restoredRoles.push(role);
      }
    }
    if (restoredRoles.length === 0) return state;
    sig._restored_from_backup = restoredRoles;
    return { ...state, signatures: sig };
  } catch {
    return state;
  }
}

export const INITIAL_STATE = {
  agency: {
    name: "", provider_number: "", npi: "", address: "",
    phone: "", fax: "", contact_name: "",
  },
  client: {
    first_name: "", last_name: "", middle_initial: "", date_of_birth: "", gender: "",
    ssn_last4: "",
    race: "", language_preferred: "English", marital_status: "",
    address_street: "", address_city: "", address_state: "", address_zip: "",
    county_of_residence: "", county_of_responsibility: "",
    phone_primary: "", phone_work: "", phone_cell: "",
    emergency_contact: "", emergency_phone: "",
    legal_rep: "",
    insurance_type: "",
    forwardhealth_member_id: "",
    mco_name: "", mco_member_id: "",
    medical_record_number: "",
    assessment_date: "", assessment_location: "Client home",
    living_situation: "", living_situation_other: "",
  },
  insurance: {
    types: [],
    medicaid_number: "",
    ltc_number: "", railroad_number: "",
    other_name: "", other_policy: "",
  },
  responsible_party: {
    relationship: "", name: "",
    phone_home: "", phone_work: "", phone_cell: "",
    best_time: "", address: "",
  },
  physician: { name: "", npi: "", address: "", phone: "", fax: "" },
  skilled_visits: [],
  adl: {
    bathing: { pcst_level: "", score: "", days_per_week: "", frequency: "", assistance_type: "", notes: "" },
    dressing: { pcst_level: "", score: "", days_per_week: "", frequency: "", assistance_type: "", notes: "" },
    dressing_upper: { pcst_level: "", days_per_week: "", time_of_day: "" },
    dressing_lower: { pcst_level: "", days_per_week: "", time_of_day: "" },
    grooming: { pcst_level: "", days_per_week: "", time_of_day: "" },
    eating: { pcst_level: "", score: "", days_per_week: "", meals: [], days_breakfast: "", days_lunch: "", days_dinner: "", frequency: "", assistance_type: "", notes: "" },
    mobility: { pcst_level: "", score: "", days_per_week: "", frequency: "", assistance_type: "", notes: "" },
    toileting: { pcst_level: "", score: "", days_per_week: "", frequency_per_day: "", frequency: "", assistance_type: "", notes: "" },
    transferring: { pcst_level: "", score: "", days_per_week: "", frequency: "", assistance_type: "", notes: "" },
    prosthetics: { needed: "", days_per_week: "", items: "" },
  },
  iadl: {
    meal_prep: { score: "", notes: "" }, housekeeping: { score: "", notes: "" },
    laundry: { score: "", notes: "" }, shopping: { score: "", notes: "" },
    medication_mgmt: { score: "", notes: "" }, money_mgmt: { score: "", notes: "" },
    phone_use: { score: "", notes: "" }, transportation: { score: "", notes: "" },
  },
  nursing: {
    medications_admin: "", injectables: "", wound_care: "",
    catheter_care: "", ostomy_care: "",
    delegated_acts: [], incidental: [], notes: "",
    medication_assistance: {
      level: "", frequency_per_day: "", days_per_week: "",
      // Wave-2 detail:
      self_responsible: "",     // Yes / No
      needs_reminders: "",      // Yes / No
      reminders_per_day: "",
      pillbox_used: "",         // Yes / No
      pillbox_weeks_prefilled: "",
      pillbox_managed_by: "",
      resistant_to_meds: "",    // Yes / No
      physical_mental_unable: "",
      schedule_sheet_present: "",  // Yes / No
    },
    part_ii: {},
    part_iii: {},
  },
  communication: {
    communication: "", capability_code: "",
    orientation: "", cognition: "",
    vision: "", hearing: "", speech: "", behavior: "",
  },
  hearing_aid: { wears_aid: "", ability_code: "" },
  vision_correction: { wears_lenses: "", ability_code: "" },
  orientation: { level: "", forgetfulness: [], disorientation: [] },
  medical_history: {
    chief_complaint: "", past_history: "", surgical_history: "",
    allergies: "", drug_allergies: "", food_allergies: "",
    hospitalizations: "", falls: "",
  },
  diagnoses: [{ icd10: "", description: "", date: "" }],
  medications: [{ name: "", dose: "", frequency: "", route: "", start_date: "", end_date: "", prescriber: "" }],
  social: {
    lives_with: "", home_environment: "",
    financial: "", engagement: "",
  },
  activity_schedule: [],
  staffing_schedule: [],
  cms485: {
    start_of_care_date: "", cert_period_from: "", cert_period_to: "",
    cert_type: "Certification", dme_supplies: "", safety_measures: "",
    functional_limitations: [],
    orders_treatments: "", rehab_potential: "", goals_discharge: "",
    nurse_verbal_soc: "", hha_received_date: "", physician_sign_date: "",
  },
  clinical_summary: {
    weekly_hours: "", visits_per_week: "", auth_period: "",
    rationale: "", risks_if_denied: "", reassessment_plan: "",
  },
  // Non-Skilled Assessment (NSA) — comprehensive, non-OASIS clinical snapshot.
  assessment: {
    // Vitals
    vitals: {
      bp_systolic: "", bp_diastolic: "", hr: "", rr: "", temp: "", spo2: "", blood_glucose: "",
      vitals_date: "", vitals_time: "", baseline_notes: "",
    },
    // Anthropometrics & nutritional
    anthro: {
      height_in: "", weight_lb: "", bmi: "",
      recent_weight_loss_lb: "", recent_weight_loss_period: "",
    },
    nutrition: {
      diet: "",              // Regular / Diabetic / Low-sodium / Renal / Mechanical soft / Puree / Thickened
      appetite: "",          // Good / Fair / Poor
      intake: "",            // >75% / 50–75% / 25–50% / <25%
      fluid_intake: "",      // cups/day
      swallowing_issues: "", // Yes / No
      feeding_tube: "",      // None / G-tube / J-tube / NG
      must_score: "",        // 0 / 1 / ≥2
      notes: "",
    },
    // Pain (PQRST)
    pain: {
      present: "",           // Yes / No
      severity: "",          // 0–10
      location: "",
      quality: "",           // sharp / dull / burning
      timing: "",            // constant / intermittent
      provocation: "",
      relief: "",
      interferes_adls: "",   // Yes / No
    },
    // Braden Scale — 6 items × 1–4 (perception 1-4, activity 1-4, etc)
    braden: {
      sensory_perception: "", moisture: "", activity: "",
      mobility: "", nutrition: "", friction_shear: "",
      total: "",            // computed
      risk_category: "",    // computed — Mild/Moderate/High/Very High
      notes: "",
    },
    // Morse Fall Scale
    morse: {
      history_of_falls: "",           // 0 / 25
      secondary_diagnosis: "",        // 0 / 15
      ambulatory_aid: "",             // 0 / 15 / 30
      iv_therapy: "",                 // 0 / 20
      gait: "",                       // 0 / 10 / 20
      mental_status: "",              // 0 / 15
      total: "",                      // computed
      risk_category: "",              // No / Low / High
      interventions: "",
    },
    // Skin integrity
    skin: {
      intact: "",             // Yes / No
      breakdown_sites: "",    // free text
      pressure_injuries: [],  // [{site, stage, size, treatment}]
      turgor: "",             // Good / Fair / Poor
      color: "",              // WNL / Pale / Jaundiced / Cyanotic
      notes: "",
    },
    // Continence baseline
    continence: {
      bladder_pattern: "",   // Continent / Occasional / Frequent / Incontinent / Catheter / Ostomy
      bowel_pattern: "",     // Regular / Constipation / Diarrhea / Incontinent / Colostomy
      last_bm: "",
      bladder_schedule: "",  // Timed voiding schedule
      bowel_program: "",
      notes: "",
    },
    // Advance directives
    directives: {
      living_will: "",          // Yes / No / Unknown
      living_will_copy_provided: "", // Yes / No
      living_will_copy_requested: "",// Yes / No
      dnr: "",                  // Yes / No / Unknown
      dnr_copy_provided: "",    // Yes / No
      polst: "",                // Yes / No / Unknown
      polst_copy_provided: "",  // Yes / No
      healthcare_poa: "",       // Yes / No / Unknown
      poa_name: "",
      poa_phone: "",
      location_of_documents: "",
      notes: "",
    },
    // Immunizations — each: given (Yes/No/Unknown) + date
    immunizations: {
      tetanus: { given: "", date: "" },
      pneumonia: { given: "", date: "" },
      hepatitis: { given: "", date: "" },
      influenza: { given: "", date: "" },
      h1n1: { given: "", date: "" },
      notes: "",
    },
    // Prognosis + ER triggers + patient/family understanding
    prognosis: {
      prognosis: "",                    // Excellent / Good / Guarded / Poor / Terminal
      family_understands_dx: "",        // Yes / Partially / No
      recent_er_visits: "",             // count/text
      er_triggers: "",                  // free text (what makes patient want/need ER)
    },
    // Caregiver capability (distinct from burden) + family
    caregiver_capability: {
      family_supportive: "",            // Yes / No / Unknown
      caregiver_able_willing: "",       // Yes / No
      caregiver_follows_instructions: "", // Yes / No
      caregiver_assists_adls: "",       // Yes / No
      caregiver_safe: "",               // Yes / No
      notes: "",
    },
    // Oxygen safety (branches: only show if assessment.emergency.oxygen_in_home === "Yes")
    oxygen_safety: {
      sign_posted: "",               // Yes / No
      eight_feet_from_flame: "",     // Yes / No
      backup_tank: "",               // Yes / No
    },
    // Safety & abuse screening — default all "No"; nurse flips what's positive.
    safety_screen: {
      suspected_neglect: "",
      suspected_physical_abuse: "",
      suspected_financial_abuse: "",
      inadequate_cooking_shopping: "",
      insects_rodents: "",
      left_unattended_when_needs_supervision: "",
      msw_referral_made: "",
      community_resources_needed: "",
      notes: "",
    },
    // Financial status
    financial: {
      finances_handling: "",          // Independent / Needs assistance / Totally dependent
      inappropriate_income_use: "",   // Yes / No
      inadequate_for_necessities: "", // Yes / No
      uncovered_medical_expenses: "", // Yes / No
      notes: "",
    },
    // Teaching materials checklist — patient/family received
    teaching: {
      rights_responsibilities: false,
      advance_directives: false,
      dnr_hospice: false,
      hipaa: false,
      oasis_privacy: false,
      agency_contact_info: false,
      when_to_contact_nurse_md: false,
      infection_precautions: false,
      home_safety: false,
      sharps_disposal: false,
      medication_safety: false,
      emergency_preparedness: false,
      notes: "",
    },
    // MAHC-10 Fall Risk (10 Y/N items, 1 pt each, ≥4 = high risk)
    mahc10: {
      age_65_plus: "",                // auto from DOB
      diagnoses_three_plus: "",       // auto from diagnoses.length
      prior_falls_3mo: "",
      incontinence: "",               // auto from continence
      visual_impairment: "",          // auto from communication.vision
      polypharmacy: "",               // auto from medications.length
      pain_affecting_function: "",    // auto from pain.interferes_adls
      cognitive_impairment: "",
      mobility_impairment: "",
      environmental_hazards: "",
      notes: "",
    },
    // GG-style functional scores (CMS-style 0-3 / 0-5 / 0-6 per item)
    gg_functional: {
      grooming: "",              // 0-3
      dress_upper: "",           // 0-3
      dress_lower: "",           // 0-3
      bathing: "",               // 0-6
      toilet_transfer: "",       // 0-4
      toileting_hygiene: "",     // 0-3
      transfer: "",              // 0-5
      ambulation: "",            // 0-6
      eating: "",                // 0-5
      meal_prep_light: "",       // 0-2
      telephone: "",             // 0-5 or NA
    },
    // Prior functioning (before current episode)
    prior_functioning: {
      self_care: "",       // Independent / Some Help / Dependent
      ambulation: "",
      transfer: "",
      household_tasks: "",
      multi_factor_fall_done: "",    // Yes / No
      multi_factor_fall_risk: "",    // Yes / No  (only if done=Yes)
    },
    // Sensory — nose / pharynx / mouth detail (vision + hearing are in Step 4)
    sensory: {
      nasal_congestion: "",       // Yes / No
      loss_of_smell: "",          // Yes / No
      pharynx_hoarseness: "",     // Yes / No
      pharynx_sore_throat: "",    // Yes / No
      mouth_status: "",           // Normal / Abnormal oral mucosa / Dentures / Gum problem
      dentures: "",               // None / Upper / Lower / Full
      oral_notes: "",
    },
    // Lifestyle — smoking / alcohol with degree
    lifestyle: {
      smokes: "",             // Yes / No
      smoking_degree: "",     // Light / Moderate / Heavy
      smoking_pack_years: "",
      alcohol: "",            // Yes / No
      alcohol_degree: "",     // Occasional / Moderate / Heavy
      alcohol_drinks_per_week: "",
      notes: "",
    },
    // Weekly cognitive / behavioral symptom checklist (Y/N per item)
    behaviors_weekly: {
      memory_deficit: "",
      impaired_decision_making: "",
      verbal_disruption: "",
      physical_aggression: "",
      disruptive_behavior: "",
      delusional_hallucinatory: "",
      none: "",               // Yes = none demonstrated
      notes: "",
    },
    // Extended continence detail (builds on assessment.continence)
    continence_detail: {
      bladder_freq_per_day: "",
      bladder_wears_briefs: "",       // Yes / No
      bowel_freq_per_day: "",
      bowel_wears_briefs: "",
      raised_toilet_seat: "",         // Yes / No
      assist_buttons_zippers: "",     // Yes / No
    },
    // IADL expansion: meal/shopping/driving/housekeeping
    iadl_detail: {
      meal_prep_assist: "",          // None / Partial / Full
      feeding_assist: "",
      shopping_assist: "",
      cutting_food: "",              // Yes / No
      pureeing_food: "",             // Yes / No
      favorite_foods: "",
      mealtimes: { breakfast: "", lunch: "", dinner: "", snacks: "" },
      supplements: "",
    },
    // Transportation
    transportation: {
      patient_drives: "",                // Yes / No
      transport_mode: "",                // Private vehicle / Public / Independent / Must be driven / Must be accompanied / Ambulance
      transport_needed_for_appointments: "",  // Yes / No
      caregiver_drives_patient_car: "",  // Yes / No
      notes: "",
    },
    // Shopping & Housekeeping capability
    housekeeping: {
      shopping_capability: "",      // Able all / Small only / With assistance / Unable / No opportunity / Chooses not
      housekeeping_capability: "",  // Independent / Help w/ heavy / Light only / Regular help / Unable / Chooses not
      tasks_needing_help: [],       // multi-select list
      notes: "",
    },
    // Activities
    activities: {
      permitted: [],                // Bedrest / Up as tolerated / Exercises / Assistive devices / No restrictions
      favorite_home: "",
      favorite_away: "",
      restrictions_notes: "",
    },
    // General notes + patient/rep signature (for NSA PDF bottom)
    general_notes: "",
    patient_signature: {
      name: "",
      date: "",
      relationship_to_patient: "",  // Self / Authorized Rep
    },
    // Caregiver burden (5-item Zarit-lite: 0 never, 1 rarely, 2 sometimes, 3 quite frequently, 4 nearly always)
    caregiver: {
      primary_name: "",
      primary_relationship: "",
      primary_phone: "",
      hours_per_week: "",
      z1_too_much: "",          // 0–4
      z2_no_time_self: "",
      z3_stressed_combining: "",
      z4_strained_caregiving: "",
      z5_burden: "",
      total: "",                // computed
      respite_requested: "",    // Yes / No
      notes: "",
    },
    // Emergency preparedness
    emergency: {
      evacuation_plan: "",          // Yes / No
      power_dependent_equipment: [], // list of equipment names (oxygen, vent, feeding pump, CPAP)
      backup_power_plan: "",
      evacuation_assistance_needed: "",
      fire_hazard_notes: "",
      oxygen_in_home: "",           // Yes / No
      smoke_detectors: "",          // Yes / No
      emergency_contact_chain: "",  // free text
      nearest_er: "",
    },
    // Mental / behavioral snapshot (complements Step 4 communication)
    mood: {
      mood: "",                 // Euthymic / Anxious / Depressed / Labile
      affect: "",
      depression_screen_phq2: "", // 0–6
      anxiety_notes: "",
      safety_ideation: "",        // None / Passive / Active (trigger referral)
      notes: "",
    },
    // Summary narrative
    summary_narrative: "",
  },
  rn_checklist: {},
  ai_review: null,
  signatures: {
    rn_name: "", rn_license: "", rn_date: "", rn_signature_png: "",
    agency_rep_name: "", agency_rep_title: "", agency_rep_date: "", agency_rep_signature_png: "",
    client_name: "", client_sign_date: "", client_signature_png: "",
    pcw_name: "", pcw_sign_date: "", pcw_signature_png: "",
  },
};

const WizardCtx = createContext(null);

function merge(base, patch) {
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) return patch;
  const out = { ...base };
  for (const k of Object.keys(patch)) {
    const v = patch[k];
    if (v && typeof v === "object" && !Array.isArray(v) && base && typeof base[k] === "object" && !Array.isArray(base[k])) {
      out[k] = merge(base[k] || {}, v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export function WizardProvider({ children }) {
  const [data, setData] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
      const base = raw ? merge(INITIAL_STATE, JSON.parse(raw)) : INITIAL_STATE;
      return hydrateSignatureBackup(base);
    } catch {
      return INITIAL_STATE;
    }
  });

  // Avida Care pre-filled-field tracking (Prompt 6B).
  //   - Populated by `loadAvidaDraft()` when the RN deep-links from Avida.
  //   - Each entry is a dotted path like "client.first_name" or "physician.name".
  //   - The `update()` callback clears any path it overwrites, so the blue
  //     "pre-filled" tint disappears the moment the RN edits a field (which
  //     is how we mark fields as RN-verified per the spec).
  //   - Persisted in sessionStorage (not localStorage) because the tint is a
  //     per-tab UX affordance; a new tab on the same device should re-fetch
  //     the draft and rebuild the set from scratch.
  const [prefilledFields, setPrefilledFields] = useState(() => {
    try {
      const raw = window.sessionStorage.getItem("wi-pa-avida-prefilled-v1");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        "wi-pa-avida-prefilled-v1",
        JSON.stringify([...prefilledFields]),
      );
    } catch { /* sessionStorage unavailable — non-fatal */ }
  }, [prefilledFields]);

  const [avidaDraftId, setAvidaDraftId] = useState(() => {
    try { return window.sessionStorage.getItem("wi-pa-avida-draft-id-v1") || ""; }
    catch { return ""; }
  });
  useEffect(() => {
    try {
      if (avidaDraftId) window.sessionStorage.setItem("wi-pa-avida-draft-id-v1", avidaDraftId);
      else window.sessionStorage.removeItem("wi-pa-avida-draft-id-v1");
    } catch { /* non-fatal */ }
  }, [avidaDraftId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch { /* localStorage unavailable — non-fatal */ }
  }, [data]);

  // Mid-flight server checkpoint — when a parent-app visit is active, push
  // the wizard state to /api/visits/{visit_id}/draft every ~5 seconds of
  // changes. If the RN's tablet dies in-home, ANY device opening the same
  // visit_id resumes from the latest checkpoint (instead of losing data).
  // Debounced via a useRef'd timer so rapid keystrokes coalesce into one
  // PUT after the user pauses typing.
  const checkpointTimer = React.useRef(null);
  useEffect(() => {
    let visitId = null;
    try {
      const raw = window.localStorage.getItem("wi-pa-active-visit-v1");
      if (raw) visitId = (JSON.parse(raw) || {}).visit_id || null;
    } catch { /* non-fatal */ }
    if (!visitId) return undefined;
    if (checkpointTimer.current) clearTimeout(checkpointTimer.current);
    checkpointTimer.current = setTimeout(async () => {
      try {
        const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
        // Strip the heavy/transient fields — we don't need to ship the
        // entire AI conversation history or the cached signature backup
        // through the wire on every change. Signatures themselves DO go
        // (they're the most important thing to checkpoint).
        const { ai_review, ...rest } = data || {};
        await fetch(`${apiBase}/visits/${visitId}/draft`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ state: rest, current_step: 1 }),
          keepalive: true,
        });
      } catch { /* network blip — non-fatal, next change tries again */ }
    }, 5000);
    return () => {
      if (checkpointTimer.current) clearTimeout(checkpointTimer.current);
    };
  }, [data]);

  const update = useCallback((section, patch) => {
    setData((prev) => {
      const nextSection = merge(prev[section] || {}, patch);
      const nextState = { ...prev, [section]: nextSection };
      // Auto-cascade: Agency -> Billing Provider (PCST Section VII) if empty
      if (section === "agency") {
        const bp = prev.billing_provider || {};
        nextState.billing_provider = {
          ...bp,
          name: bp.name || nextSection.name || "",
          provider_number: bp.provider_number || nextSection.provider_number || "",
          address: bp.address || nextSection.address || "",
        };
      }
      // Auto-cascade: RN signer -> PCST screener signature (RN acts as screener)
      if (section === "signatures") {
        const ss = prev.screener_signature || {};
        nextState.screener_signature = {
          ...ss,
          rn_name: ss.rn_name || nextSection.rn_name || "",
          rn_license: ss.rn_license || nextSection.rn_license || "",
          rn_date: ss.rn_date || nextSection.rn_date || "",
          rn_signature_png: ss.rn_signature_png || nextSection.rn_signature_png || "",
          qualification: ss.qualification || "RN",
        };
      }
      return nextState;
    });
    // Clear the Avida "pre-filled" tint on any field the RN just edited.
    // `patch` is a flat {key:value} shape because every Step field calls
    // `update("client", {first_name: v})` — one key per call — so the
    // dotted path is deterministic.
    setPrefilledFields((prev) => {
      if (prev.size === 0) return prev;   // nothing to clear — avoid a re-render
      const next = new Set(prev);
      let changed = false;
      for (const key of Object.keys(patch || {})) {
        if (next.delete(`${section}.${key}`)) changed = true;
      }
      return changed ? next : prev;
    });
  }, []);

  const setSection = useCallback((section, value) => {
    setData((prev) => ({ ...prev, [section]: value }));
  }, []);

  const reset = useCallback(() => {
    setData(INITIAL_STATE);
    setPrefilledFields(new Set());
    setAvidaDraftId("");
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* localStorage unavailable — non-fatal */ }
    try { window.sessionStorage.removeItem("wi-pa-avida-prefilled-v1"); } catch { /* non-fatal */ }
    try { window.sessionStorage.removeItem("wi-pa-avida-draft-id-v1"); } catch { /* non-fatal */ }
    // Also drop the Copy-from-last baseline so the recert diff card doesn't
    // show stale changes against a cleared draft.
    try { window.localStorage.removeItem("wi-pa-copy-source-v1"); } catch { /* non-fatal */ }
    // An explicit reset is the ONE intentional "wipe everything" action.
    // Clear signature backups so the next visit starts truly empty.
    clearAllSignatureBackups();
  }, []);

  const loadSample = useCallback((sample) => {
    setData(merge(INITIAL_STATE, sample));
  }, []);

  /**
   * Hydrate the wizard from a Prompt-6B Avida Care seed-draft.
   * Called by `pages/Wizard.jsx` when the URL has `?draft=<id>&source=avida_care`.
   *
   * `draft` is the JSON body from `GET /api/intake/drafts/{id}`:
   *   { draft_id, wizard_state: { client, physician, specialists }, prefilled_fields }
   *
   * Behaviour:
   *   - Merges `draft.wizard_state` into INITIAL_STATE (doesn't stomp on
   *     clinical sections the RN may have already started).
   *   - Seeds the prefilledFields set so every coordinator-supplied field
   *     renders with the blue tint + "Pre-filled from Avida Care" label.
   *   - Remembers the draft_id so the Step 10 completion hook can trigger
   *     the outbound webhook back to Avida.
   */
  const loadAvidaDraft = useCallback((draft) => {
    if (!draft || !draft.draft_id) return;
    const seed = draft.wizard_state || {};
    setData((prev) => {
      // Start from INITIAL_STATE (not prev) so a stale localStorage draft
      // from a different client doesn't leak in. Agency block: when the
      // seed payload includes one (Avida Care now ships organization
      // details inline), that wins — RN cannot edit it. When seed.agency
      // is empty/missing, we fall back to the previous agency (e.g. a
      // saved-profile selection from a prior session).
      const seedAgency = seed.agency || {};
      const seedHasAgency = Object.values(seedAgency).some(
        (v) => v !== "" && v != null,
      );
      const seedInsurance = seed.insurance || {};
      const base = {
        ...INITIAL_STATE,
        agency: seedHasAgency ? { ...INITIAL_STATE.agency, ...seedAgency } : prev.agency,
        // Insurance block: same pattern as agency. Always overlay any
        // seeded fields onto INITIAL_STATE.insurance so the lock toggles
        // in Avida flow even when coordinator omits a field.
        insurance: { ...INITIAL_STATE.insurance, ...seedInsurance },
      };
      return merge(base, {
        client: seed.client || {},
        physician: seed.physician || {},
        // Specialists is a flat list the wizard exposes as reference data;
        // store under a non-colliding key so existing steps ignore it.
        avida_specialists: seed.specialists || [],
      });
    });
    setPrefilledFields(new Set(draft.prefilled_fields || []));
    setAvidaDraftId(draft.draft_id);
  }, []);

  const isPrefilled = useCallback(
    (section, key) => prefilledFields.has(`${section}.${key}`),
    [prefilledFields],
  );

  // Recert verified-readonly mode (Phase 6C). Triggers when the wizard
  // was seeded from a previous submission via "Copy from last visit"
  // (`wi-pa-copy-source-v1` localStorage entry written by
  // CopyFromLastModal) OR when the URL carries `?recert=1`. Section B
  // demographics get a green "Verified from <date>" treatment by
  // default; the RN can flip `recertOverride` on to unlock the entire
  // block when something genuinely changed (move, name change, etc.).
  // Read once on mount — the signals are set BEFORE the wizard renders
  // (CopyFromLastModal writes localStorage then closes; the URL param
  // is decided at navigation time).
  const [recertSource] = useState(() => {
    // Either signal activates recert mode. The localStorage entry (set
    // by CopyFromLastModal) carries the date + client_name; the URL
    // param `?recert=1` only flips the toggle. We always read storage
    // when present so the date label renders even with both signals.
    let urlActive = false;
    try {
      const url = new URL(window.location.href);
      urlActive = url.searchParams.get("recert") === "1";
    } catch { /* SSR / invalid URL */ }
    try {
      const raw = window.localStorage.getItem("wi-pa-copy-source-v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          active: true,
          copiedAt: Number(parsed?.copied_at) || 0,
          clientName: parsed?.client_name || "",
        };
      }
    } catch { /* unavailable */ }
    if (urlActive) return { active: true, copiedAt: 0, clientName: "" };
    return { active: false, copiedAt: 0, clientName: "" };
  });

  const recertMode = recertSource.active;
  const recertSourceLabel = useMemo(() => {
    if (!recertSource.copiedAt) return "";
    try {
      return new Date(recertSource.copiedAt).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch { return ""; }
  }, [recertSource.copiedAt]);

  const [recertOverride, setRecertOverride] = useState(false);
  // Sticky per-tab so navigating between steps doesn't snap fields back
  // to locked while the RN is editing.
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem("wi-pa-recert-override-v1");
      if (raw === "1") setRecertOverride(true);
    } catch { /* unavailable */ }
  }, []);
  useEffect(() => {
    try {
      if (recertOverride) {
        window.sessionStorage.setItem("wi-pa-recert-override-v1", "1");
      } else {
        window.sessionStorage.removeItem("wi-pa-recert-override-v1");
      }
    } catch { /* non-fatal */ }
  }, [recertOverride]);

  const value = useMemo(() => ({
    data, update, setSection, reset, loadSample, setData,
    // Avida Care pre-fill tracking
    prefilledFields, isPrefilled, loadAvidaDraft,
    avidaDraftId, setAvidaDraftId,
    // Recert verified-readonly mode
    recertMode, recertSourceLabel, recertOverride, setRecertOverride,
  }), [
    data, update, setSection, reset, loadSample,
    prefilledFields, isPrefilled, loadAvidaDraft, avidaDraftId,
    recertMode, recertSourceLabel, recertOverride,
  ]);
  return <WizardCtx.Provider value={value}>{children}</WizardCtx.Provider>;
}

export function useWizard() {
  const ctx = useContext(WizardCtx);
  if (!ctx) throw new Error("useWizard must be used within WizardProvider");
  return ctx;
}
